import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, timeout } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import xxhash from 'xxhash-wasm';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { IESHit, IESMGetResponse, IESResponse } from '../../shared/interfaces/elastic-search.interface';

@Injectable({
  providedIn: 'root',
})
export class QueryHashService {
  constructor(private readonly httpClient: HttpClient) {}

  getAccountsAndSitesByHashes(
    accountIdsHash: string,
    siteIdsHash: string,
  ): Observable<{
    accountIds: number[];
    siteIds: number[];
  }> {
    return this.httpClient
      .get<IESMGetResponse<IHashTableRecord>>(`${environment.elastic_search_url}/hash-store/_mget`, {
        params: {
          source_content_type: 'application/json',
          source: JSON.stringify({ ids: [accountIdsHash, siteIdsHash] }),
        },
        headers: new HttpHeaders({
          Authorization: `ApiKey ${environment.elastic_search_api_key}`,
        }),
      })
      .pipe(
        timeout({ first: 5000 }),
        map(({ docs }) => this.getValuesFromESRecords(docs, accountIdsHash, siteIdsHash)),
      );
  }

  async saveAccountsAndSites(
    siteIds: number[],
    accountIds: number[],
  ): Promise<{
    accountIdsHash: string;
    siteIdsHash: string;
  }> {
    if (!Array.isArray(siteIds) || !Array.isArray(accountIds)) {
      throw new Error('SiteIds and/or accountIds is not an array');
    }
    const { h64ToString } = await xxhash();
    const sortedSiteIds = [...siteIds].sort((a1, b1) => a1 - b1).toString();
    const sortedAccountIds = [...accountIds].sort((a1, b1) => a1 - b1).toString();
    const siteIdsHash = h64ToString(sortedSiteIds);
    const accountIdsHash = h64ToString(sortedAccountIds);
    const bulkPayload = this.getBulkUpdateRequestPayload(siteIdsHash, sortedSiteIds, accountIdsHash, sortedAccountIds);

    return firstValueFrom(
      this.httpClient
        .post<IESResponse>(`${environment.elastic_search_url}/_bulk?refresh=wait_for`, bulkPayload, {
          headers: new HttpHeaders({
            Authorization: `ApiKey ${environment.elastic_search_api_key}`,
            'Content-Type': 'application/json',
          }),
        })
        .pipe(map(() => ({ accountIdsHash, siteIdsHash }))),
    );
  }

  private getValuesFromESRecords(
    hits: IESHit<IHashTableRecord>[],
    accountIdsHash: string,
    siteIdsHash: string,
  ): {
    accountIds: number[];
    siteIds: number[];
  } {
    const accountIds = hits
      .find(({ _id, _source: { value } }) => _id === accountIdsHash && value)
      ?._source.value.split(',')
      .map(Number);
    const siteIds = hits
      .find(({ _id, _source: { value } }) => _id === siteIdsHash && value)
      ?._source.value.split(',')
      .map(Number);

    if (!accountIds?.length || !siteIds?.length) throw new Error('Query values were not found');

    return { accountIds, siteIds };
  }

  private getBulkUpdateRequestPayload(
    siteIdsHash: string,
    siteIds: string,
    accountIdsHash: string,
    accountIds: string,
  ): string {
    return `
     { "index": { "_index": "hash-store", "_id": "${siteIdsHash}" } }
     { "value": "${siteIds}" }
     { "index": { "_index": "hash-store", "_id": "${accountIdsHash}" } }
     { "value": "${accountIds}" }
    `.concat('\n');
  }
}

export interface IHashTableRecord {
  value: string;
}
